import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ChangeWard = () => {
    const navigate = useNavigate();
    const { 
        appId, 
        language, 
        userId, 
        mobileNo,
        userName: currentUserName,
        userType: currentUserType,
        vidhanSabhaNo: currentVidhanSabhaNo,    
        vidhanSabhaName: currentVidhanSabhaName,
        logout,
        setAppId,
        setUserName,
        setUserPhoto,
        setUserId,
        setTotalVoters,
        setVidhanSabhaNo,
        setVidhanSabhaName,
        setCandidateNo,
        setCandidateName,
        setPartyShortName,
        setPartyFullName,
        setPartyLogoPng,
        setPartyLogoJpg,
        setSlipMessage,
        setSmsSlipMessage,
        setOfflineStatus,
        setOfflineDbUrl,
        setOfflineVer,
        setSplaceUrl,
        setAppLink,
        setVideoLink,
        setAppVer,
        setBoothNo,
        setTempStatus,
        setPopupStatus,
        setPopupUrl,
        setUserType,
        setIsAuthenticated,
        setMobileNo
    } = useAppId();
    
    const [wardList, setWardList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [switching, setSwitching] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [selectedWard, setSelectedWard] = useState(null);

    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            title: "Change Ward",
            subtitle: "Switch between different wards",
            selectWard: "Select New Ward",
            currentWard: "Current Ward",
            newWard: "New Ward",
            switchWard: "Switch Ward",
            switching: "Switching...",
            back: "Back",
            dashboard: "Dashboard",
            noWards: "No other wards available for this mobile number",
            loading: "Loading available wards...",
            wardChanged: "Ward changed successfully!",
            sameWard: "You are already in this ward",
            logoutAndLogin: "Please login again to see changes",
            wardNo: "Ward No",
            wardName: "Ward Name",
            selectNewWard: "Choose a different ward to manage.",
            allWards: "All wards associated with this mobile number",
            responsibility: "Responsibility",
            selectProfile: "Select Profile"
        },
        hi: {
            title: "वार्ड बदलें",
            subtitle: "विभिन्न वार्डों के बीच स्विच करें",
            selectWard: "नया वार्ड चुनें",
            currentWard: "वर्तमान वार्ड",
            newWard: "नया वार्ड",
            switchWard: "वार्ड बदलें",
            switching: "बदला जा रहा है...",
            back: "वापस",
            dashboard: "डैशबोर्ड",
            noWards: "इस मोबाइल नंबर के लिए कोई अन्य वार्ड उपलब्ध नहीं है",
            loading: "उपलब्ध वार्ड लोड हो रहे हैं...",
            wardChanged: "वार्ड सफलतापूर्वक बदला गया!",
            sameWard: "आप पहले से ही इस वार्ड में हैं",
            logoutAndLogin: "परिवर्तन देखने के लिए कृपया फिर से लॉगिन करें",
            wardNo: "वार्ड नंबर",
            wardName: "वार्ड का नाम",
            selectNewWard: "प्रबंधित करने के लिए एक अलग वार्ड चुनें।",
            allWards: "इस मोबाइल नंबर से जुड़े सभी वार्ड",
            responsibility: "जिम्मेदारी",
            selectProfile: "प्रोफ़ाइल चुनें"
        },
        gu: {
            title: "વોર્ડ બદલો",
            subtitle: "વિવિધ વોર્ડ વચ્ચે સ્વિચ કરો",
            selectWard: "નવો વોર્ડ પસંદ કરો",
            currentWard: "વર્તમાન વોર્ડ",
            newWard: "નવો વોર્ડ",
            switchWard: "વોર્ડ બદલો",
            switching: "બદલાઈ રહ્યું છે...",
            back: "પાછા",
            dashboard: "ડેશબોર્ડ",
            noWards: "આ મોબાઇલ નંબર માટે કોઈ અન્ય વોર્ડ ઉપલબ્ધ નથી",
            loading: "ઉપલબ્ધ વોર્ડ લોડ થઈ રહ્યા છે...",
            wardChanged: "વોર્ડ સફળતાપૂર્વક બદલાયો!",
            sameWard: "તમે પહેલાથી જ આ વોર્ડમાં છો",
            logoutAndLogin: "ફેરફારો જોવા માટે કૃપા કરીને ફરીથી લૉગિન કરો",
            wardNo: "વોર્ડ નંબર",
            wardName: "વોર્ડ નામ",
            selectNewWard: "મેનેજ કરવા માટે એક અલગ વોર્ડ પસંદ કરો.",
            allWards: "આ મોબાઇલ નંબર સાથે સંકળાયેલા તમામ વોર્ડ",
            responsibility: "જવાબદારી",
            selectProfile: "પ્રોફાઇલ પસંદ કરો"
        }
    };

    const txt = t[lang];

    // Get user type label for display
    const getUserTypeLabel = (type) => {
        const map = {
            'A': 'Admin',
            'SA': 'Sub Admin',
            'sa': 'Sub Admin',
            'K': 'Volunteer',
            'k': 'Volunteer',
            'BP': 'Booth Pramukh',
            'BS': 'Sah Booth Pramukh',
            'SP': 'Shakti Kendra Pramukh',
        };
        return map[type] || type || 'User';
    };

    useEffect(() => {
        if (!mobileNo) {
            setError("Mobile number not found. Please login again.");
            setLoading(false);
            return;
        }
        fetchAllWardsForMobile();
    }, [mobileNo]);

    const fetchAllWardsForMobile = async () => {
        setLoading(true);
        setError('');
        
        try {
            const response = await axios.post(
                `${BASE_URL}/api/respo`,
                { mobile_no: mobileNo } 
            );
            
            if (response.data.success && response.data.data && response.data.data.result) {
                const allUsers = response.data.data.result;
                
                const uniqueWards = [];
                const wardKeys = new Set();
                
                allUsers.forEach(user => {
                    const key = `${user.vidhansabha_no}_${user.app_id}`;
                    if (!wardKeys.has(key)) {
                        wardKeys.add(key);
                        
                        const usersInWard = allUsers.filter(u => 
                            `${u.vidhansabha_no}_${u.app_id}` === key
                        );
                        
                        let primaryUser = usersInWard[0];
                        const rolePriority = {
                            'A': 1, 'SA': 2, 'sa': 2, 'BP': 3, 'BS': 4, 'SP': 5, 'K': 6, 'k': 6
                        };
                        
                        for (const user of usersInWard) {
                            const currentPriority = rolePriority[user.user_type] || 99;
                            const bestPriority = rolePriority[primaryUser.user_type] || 99;
                            if (currentPriority < bestPriority) {
                                primaryUser = user;
                            }
                        }
                        
                        uniqueWards.push({
                            vidhansabha_no: primaryUser.vidhansabha_no,
                            vidhansabha_name: primaryUser.vidhansabha_name,
                            app_id: primaryUser.app_id,
                            user_id: primaryUser.user_id,
                            name: primaryUser.name,
                            photo_path: primaryUser.photo_path || primaryUser.photo,
                            user_type: primaryUser.user_type,
                            total_voter: primaryUser.total_voter,
                            candidate_no: primaryUser.candidate_no,
                            candidate_name: primaryUser.candidate_name,
                            party_short_name: primaryUser.party_short_name,
                            party_full_name: primaryUser.party_full_name,
                            party_logo_png: primaryUser.party_logo_png,
                            party_logo_jpg: primaryUser.party_logo_jpg,
                            slip_message: primaryUser.slip_message,
                            sms_slip_message: primaryUser.sms_slip_message,
                            offline_status: primaryUser.offline_status,
                            offline_db_url: primaryUser.offline_db_url,
                            offline_ver: primaryUser.offline_ver,
                            splace_url: primaryUser.splace_url,
                            app_link: primaryUser.app_link,
                            video_link: primaryUser.video_link,
                            app_ver: primaryUser.app_ver,
                            booth_no: primaryUser.booth_no,
                            temp_status: primaryUser.temp_status,
                            popup_status: primaryUser.popup_status,
                            popup_url: primaryUser.popup_url,
                            all_users: usersInWard 
                        });
                    }
                });
                
                const otherWards = uniqueWards.filter(ward => 
                    parseInt(ward.vidhansabha_no) !== parseInt(currentVidhanSabhaNo)
                );
                
                setWardList(otherWards);
                
                if (otherWards.length === 0) {
                    setError(txt.noWards);
                }
            } else {
                setWardList([]);
                setError(txt.noWards);
            }
        } catch (err) {
            console.error("Error fetching wards:", err);
            setError("Failed to load available wards. Please try again.");
            setWardList([]);
        } finally {
            setLoading(false);
        }
    };

    const handleWardSwitch = async (selectedWardData) => {
        if (parseInt(selectedWardData.vidhansabha_no) === parseInt(currentVidhanSabhaNo)) {
            setError(txt.sameWard);
            return;
        }

        setSwitching(true);
        setError('');
        setSuccessMessage('');

        try {
            const targetUser = selectedWardData;
            
            localStorage.setItem('appId', targetUser.app_id);
            localStorage.setItem('userName', targetUser.name);
            localStorage.setItem('userId', targetUser.user_id);
            localStorage.setItem('totalVoters', targetUser.total_voter);
            localStorage.setItem('userType', targetUser.user_type);
            localStorage.setItem('boothNo', targetUser.booth_no || '');
            localStorage.setItem('tempStatus', targetUser.temp_status || '');
            localStorage.setItem('mobileNo', mobileNo);
            localStorage.setItem('vidhanSabhaNo', targetUser.vidhansabha_no);
            localStorage.setItem('vidhanSabhaName', targetUser.vidhansabha_name);
            localStorage.setItem('candidateNo', targetUser.candidate_no);
            localStorage.setItem('candidateName', targetUser.candidate_name);
            localStorage.setItem('partyShortName', targetUser.party_short_name);
            localStorage.setItem('partyFullName', targetUser.party_full_name);
            localStorage.setItem('partyLogoPng', targetUser.party_logo_png);
            localStorage.setItem('partyLogoJpg', targetUser.party_logo_jpg);
            localStorage.setItem('slipMessage', targetUser.slip_message || '');
            localStorage.setItem('smsSlipMessage', targetUser.sms_slip_message || '');
            localStorage.setItem('offlineStatus', targetUser.offline_status);
            localStorage.setItem('offlineDbUrl', targetUser.offline_db_url);
            localStorage.setItem('offlineVer', targetUser.offline_ver || '');
            localStorage.setItem('splaceUrl', targetUser.splace_url || '');
            localStorage.setItem('appLink', targetUser.app_link || '');
            localStorage.setItem('videoLink', targetUser.video_link || '');
            localStorage.setItem('appVer', targetUser.app_ver || '');
            localStorage.setItem('popupStatus', targetUser.popup_status);
            localStorage.setItem('popupUrl', targetUser.popup_url || '');
            localStorage.setItem('userData', JSON.stringify(targetUser));
            localStorage.setItem(`credential_${targetUser.user_id}`, JSON.stringify(targetUser));
            
            setSuccessMessage(txt.wardChanged);
            
            setAppId(targetUser.app_id);
            setUserPhoto(targetUser.photo_path || targetUser.photo || null);
            setUserName(targetUser.name);
            setUserId(targetUser.user_id);
            setTotalVoters(targetUser.total_voter);
            setUserType(targetUser.user_type);
            setBoothNo(targetUser.booth_no || '');
            setTempStatus(targetUser.temp_status || '');
            setVidhanSabhaNo(targetUser.vidhansabha_no);
            setVidhanSabhaName(targetUser.vidhansabha_name);
            setCandidateNo(targetUser.candidate_no);
            setCandidateName(targetUser.candidate_name);
            setPartyShortName(targetUser.party_short_name);
            setPartyFullName(targetUser.party_full_name);
            setPartyLogoPng(targetUser.party_logo_png);
            setPartyLogoJpg(targetUser.party_logo_jpg);
            setSlipMessage(targetUser.slip_message || '');
            setSmsSlipMessage(targetUser.sms_slip_message || '');
            setOfflineStatus(targetUser.offline_status);
            setOfflineDbUrl(targetUser.offline_db_url);
            setOfflineVer(targetUser.offline_ver || '');
            setSplaceUrl(targetUser.splace_url || '');
            setAppLink(targetUser.app_link || '');
            setVideoLink(targetUser.video_link || '');
            setAppVer(targetUser.app_ver || '');
            setPopupStatus(targetUser.popup_status);
            setPopupUrl(targetUser.popup_url || '');
            
            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);
            
        } catch (err) {
            console.error("Error switching ward:", err);
            setError("Failed to switch ward. Please try again.");
        } finally {
            setSwitching(false);
        }
    };

    return (
        <div className="change-ward-container">
            <div className="change-wrapper">
                
                {/* --- LEFT PANEL --- */}
                <div className="info-section">
                    <div className="info-content">
                        
                        {/* Mobile Header (Only visible on mobile) */}
                        <div className="mobile-only w-100" style={{ flexDirection: 'column' }}>
                            <div className="mobile-header-top d-flex">
                                <div className="mobile-avatar">
                                    <img src='/favicon.jpeg' alt="Logo" />
                                </div>
                                <div className="mobile-header-text">
                                    <h2 className="info-title-mobile">{txt.title}</h2>
                                    <p className="system-access-text-mobile">{txt.selectNewWard}</p>
                                </div>
                               
                            </div>

                            {/* Mobile Combined Card */}
                            <div className="dark-card combined-dark-card">
                                <div className="dark-card-left">
                                    
                                    <div className="dark-card-text-group">
                                        <span className="dark-card-label">ACTIVE WARD</span>
                                        <span className="dark-card-title">{txt.wardNo}: {currentVidhanSabhaNo || 'N/A'}</span>
                                    </div>
                                </div>
                                <div className="dark-card-right">
                                    <span className="dark-card-label">ROLE</span>
                                    <span className="dark-card-value-blue">{getUserTypeLabel(currentUserType)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Desktop Header & Cards (Only visible on desktop) */}
                        <div className="desktop-only w-100 h-100" style={{ flexDirection: 'column', alignItems: 'center' }}>
                            <div className="info-icon">
                                <img src='/favicon.jpeg' alt="Logo" />
                            </div>
                            
                            <h2 className="info-title">{txt.title}</h2>
                            <p className="system-access-text">{txt.selectNewWard}</p>
                          
                            {/* Current Ward Card - Desktop */}
                            <div className="dark-card">
                                <span className="dark-card-label">ACTIVE WARD</span>
                                <span className="dark-card-title">{txt.wardNo}: {currentVidhanSabhaNo || 'N/A'}</span>
                                <span className="dark-card-sub">{currentVidhanSabhaName || 'N/A'}</span>
                            </div>
                            
                            {/* Current Role Card - Desktop */}
                            <div className="dark-card" style={{ marginBottom: 'auto' }}>
                                <span className="dark-card-label">{txt.responsibility}</span>
                                <span className="dark-card-value-blue">{getUserTypeLabel(currentUserType)}</span>
                            </div>
                            
                            
                        </div>

                    </div>
                </div>

                {/* --- RIGHT PANEL --- */}
                <div className="form-section">
                    <div className="form-header">
                        <h3 className="form-title">{txt.selectWard}</h3>
                        <p className="form-subtitle">{txt.selectNewWard}</p>
                    </div>

                    {loading && (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p className="loading-text">{txt.loading}</p>
                        </div>
                    )}

                    {error && !loading && (
                        <div className="error-toast">
                            <span className="error-icon">!</span>
                            <span className="error-text">{error}</span>
                            {error !== txt.noWards && (
                                <button className="retry-btn-small" onClick={fetchAllWardsForMobile}>Retry</button>
                            )}
                        </div>
                    )}

                    {successMessage && (
                        <div className="success-toast">
                            <span className="success-icon">✓</span>
                            <div className="success-content">
                                <span className="success-text">{successMessage}</span>
                                <span className="logout-note">{txt.logoutAndLogin}</span>
                            </div>
                        </div>
                    )}

                    {/* Ward List */}
                    {!loading && !successMessage && wardList.length > 0 && (
                        <>
                            <div className="ward-list-wrapper">
                                <div className="ward-list">
                                    {wardList.map((ward) => (
                                        <div
                                            key={`${ward.vidhansabha_no}_${ward.app_id}`}
                                            className={`ward-card ${selectedWard?.vidhansabha_no === ward.vidhansabha_no ? 'selected' : ''}`}
                                            onClick={() => setSelectedWard(ward)}
                                        >
                                            <div className="profile-avatar">
                                                {ward.photo_path ? (
                                                    <img src={ward.photo_path} alt={ward.name} />
                                                ) : (
                                                    <span className="avatar-initials">{ward.name?.charAt(0)}</span>
                                                )}
                                            </div>
                                            
                                            <div className="ward-info">
                                                <div className="ward-header">
                                                    <span className="ward-number">{txt.wardNo}: {ward.vidhansabha_no}</span>
                                                    <span className={`role-badge-small ${ward.user_type === 'A' ? 'admin-badge' : 'sub-admin-badge'}`}>
                                                        {getUserTypeLabel(ward.user_type)}
                                                    </span>
                                                </div>
                                                <span className="ward-name-display text-truncate">
                                                    {ward.vidhansabha_name ? (ward.vidhansabha_name.includes('Municipal Corp') ? ward.vidhansabha_name : `${ward.vidhansabha_name} Municipal Corp.`) : 'Rajkot Municipal Corp.'}
                                                </span>
                                                <span className="ward-user-name">User: {ward.name}</span>
                                            </div>
                                            
                                            {selectedWard?.vidhansabha_no === ward.vidhansabha_no && (
                                                <div className="check-mark">
                                                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div className="version-text-mobile mobile-only">v2.4.0-stable</div>
                            </div>

                            {/* Desktop Action Buttons */}
                            <div className="action-buttons desktop-only">
                                <button className="back-btn" onClick={() => navigate('/dashboard')}>
                                    <span style={{ fontSize: '18px', marginRight: '6px' }}>←</span>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: '1.2' }}>
                                        <span>Back to</span>
                                        <span>Dashboard</span>
                                    </div>
                                </button>
                                <button
                                    className="switch-btn"
                                    onClick={() => handleWardSwitch(selectedWard)}
                                    disabled={!selectedWard || switching}
                                >
                                    {switching ? <div className="loader-small"></div> : txt.switchWard}
                                </button>
                            </div>
                        </>
                    )}

                    {!loading && !successMessage && wardList.length === 0 && !error && (
                        <div className="no-wards-state">
                            <div className="no-wards-icon">🏘️</div>
                            <p>{txt.noWards}</p>
                            <button className="back-dashboard-btn" onClick={() => navigate('/dashboard')}>
                                {txt.back}
                            </button>
                        </div>
                    )}
                </div>

                {/* --- MOBILE STICKY BOTTOM BAR --- */}
                {!loading && wardList.length > 0 && (
                    <div className="mobile-action-bar mobile-only">
                        <button
                            className="mobile-switch-btn"
                            onClick={() => handleWardSwitch(selectedWard)}
                            disabled={!selectedWard || switching}
                        >
                            {switching ? <div className="loader-small"></div> : (
                                <>
                                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{marginRight: '8px'}}>
                                        <polyline points="16 3 21 3 21 8"></polyline><line x1="4" y1="20" x2="21" y2="3"></line><polyline points="21 16 21 21 16 21"></polyline><line x1="15" y1="15" x2="21" y2="21"></line><line x1="4" y1="4" x2="9" y2="9"></line>
                                    </svg>
                                    {txt.switchWard}
                                </>
                            )}
                        </button>
                        <div className="mobile-bottom-row">
                            <button className="mobile-secondary-btn" onClick={() => navigate('/dashboard')}>
                                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
                                {txt.dashboard}
                            </button>
                            <button className="mobile-secondary-btn" onClick={() => navigate(-1)}>
                                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                                {txt.back}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .change-ward-container {
                    width: 100%;
                    min-height: 100vh;
                    background: #f8fafc;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                .change-wrapper {
                    display: flex;
                    width: 100%;
                    max-width: 950px;
                    height: 520px;
                    background: #ffffff;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05);
                }

                /* Display Toggle Classes */
                .mobile-only { display: none !important; }
                .desktop-only { display: flex; }
                .w-100 { width: 100%; }
                .h-100 { height: 100%; }

                /* --- LEFT PANEL STYLES (DESKTOP) --- */
                .info-section {
                    width: 360px;
                    background: #111827; 
                    padding: 30px 24px;
                    display: flex;
                    flex-direction: column;
                }

                .info-content {
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    height: 100%;
                    width: 100%;
                }

                .info-icon {
                    width: 100px;
                    height: 100px;
                    border-radius: 50%;
                    background: #ffffff;
                    margin: 10px auto 20px;
                    position: relative;
                    box-shadow: -4px -4px 20px rgba(167, 243, 208, 0.3), 4px 4px 20px rgba(96, 165, 250, 0.4);
                }

                .info-icon img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    border-radius: 50%;
                }

                .info-title {
                    font-size: 24px;
                    font-weight: 700;
                    color: white;
                    margin: 0;
                }

                .system-access-text {
                    font-size: 13px;
                    color: #94a3b8;
                    margin-top: 6px;
                    margin-bottom: 24px;
                }

                .dark-card {
                    background: #1e293b;
                    border: 1px solid #334155;
                    border-radius: 12px;
                    padding: 14px;
                    width: 100%;
                    margin-bottom: 12px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .dark-card-label {
                    font-size: 10px;
                    font-weight: 600;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    margin-bottom: 6px;
                }

                .dark-card-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: white;
                    margin-bottom: 4px;
                }

                .dark-card-sub {
                    font-size: 12px;
                    color: #94a3b8;
                }

                .dark-card-value-blue {
                    font-size: 16px;
                    font-weight: 700;
                    color: #60a5fa; 
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }

                .version-text {
                    color: #475569;
                    font-size: 11px;
                    font-style: italic;
                    margin-top: auto;
                    padding-top: 15px;
                }

                /* --- RIGHT PANEL STYLES --- */
                .form-section {
                    flex: 1;
                    padding: 30px 40px;
                    background: white;
                    display: flex;
                    flex-direction: column;
                }

                .form-header {
                    margin-bottom: 20px;
                }

                .form-title {
                    font-size: 22px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0 0 6px;
                }

                .form-subtitle {
                    font-size: 14px;
                    color: #64748b;
                    margin: 0;
                }

                /* Ward List */
                .ward-list-wrapper {
                    flex: 1;
                    overflow-y: auto;
                    margin-bottom: 20px;
                    padding-right: 8px;
                }

                .ward-list {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                .ward-card {
                    display: flex;
                    align-items: center;
                    padding: 14px 20px;
                    border: 1px solid #f1f5f9;
                    border-radius: 12px;
                    background: #f8fafc;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .ward-card:hover {
                    border-color: #e2e8f0;
                }

                .ward-card.selected {
                    background: #ffffff;
                    border: 1.5px solid #3b82f6;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
                }

                .profile-avatar {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: #f1f5f9;
                    border: 1px solid #e2e8f0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-right: 16px;
                    flex-shrink: 0;
                    overflow: hidden;
                }

                .profile-avatar img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    border-radius: 50%;
                }

                .avatar-initials {
                    font-size: 18px;
                    font-weight: 700;
                    color: #0f172a;
                    text-transform: uppercase;
                }

                .ward-info {
                    flex: 1;
                    min-width: 0;
                }

                .ward-header {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 4px;
                }

                .ward-number {
                    font-size: 15px;
                    font-weight: 700;
                    color: #0f172a;
                }

                .role-badge-small {
                    font-size: 10px;
                    font-weight: 700;
                    padding: 2px 8px;
                    border-radius: 4px;
                    text-transform: uppercase;
                }

                .admin-badge {
                    background: #e2e8f0;
                    color: #475569;
                }

                .sub-admin-badge {
                    background: #dbeafe;
                    color: #2563eb;
                }

                .ward-name-display {
                    display: block;
                    font-size: 13px;
                    color: #475569;
                    margin-bottom: 2px;
                }

                .ward-user-name {
                    display: block;
                    font-size: 12px;
                    color: #94a3b8;
                }

                .text-truncate {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .check-mark {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    color: #1e3a8a;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                /* Buttons */
                .action-buttons {
                    display: flex;
                    gap: 16px;
                    margin-top: auto;
                }

                .back-btn {
                    flex: 0 0 auto;
                    display: flex;
                    align-items: center;
                    padding: 10px 20px;
                    background: #f1f5f9;
                    color: #475569;
                    border: none;
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .back-btn:hover {
                    background: #e2e8f0;
                }

                .switch-btn {
                    flex: 1;
                    padding: 12px;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .switch-btn:hover:not(:disabled) {
                    background: #2563eb;
                }

                .switch-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                /* States */
                .loading-state, .no-wards-state {
                    text-align: center;
                    padding: 40px 20px;
                }

                .spinner {
                    width: 40px; height: 40px;
                    border: 3px solid #e2e8f0;
                    border-top: 3px solid #3b82f6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 16px;
                }

                .loader-small {
                    width: 18px; height: 18px;
                    border: 2px solid rgba(255,255,255,0.3);
                    border-top: 2px solid white;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }

                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                .loading-text, .no-wards-state p {
                    color: #64748b;
                    font-weight: 500;
                }

                .no-wards-icon { font-size: 48px; margin-bottom: 16px; }

                .back-dashboard-btn {
                    padding: 10px 24px;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    margin-top: 16px;
                }

                .error-toast, .success-toast {
                    display: flex; align-items: center; gap: 12px;
                    padding: 12px 16px; border-radius: 8px; margin-bottom: 20px;
                }
                .error-toast { background: #fef2f2; border: 1px solid #fecaca; }
                .success-toast { background: #f0fdf4; border: 1px solid #bbf7d0; }
                .error-icon { width: 24px; height: 24px; background: #ef4444; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; flex-shrink: 0; }
                .success-icon { width: 24px; height: 24px; background: #10b981; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; flex-shrink: 0; }
                .error-text { color: #991b1b; font-size: 14px; font-weight: 500; flex: 1; }
                .success-text { color: #166534; font-size: 14px; font-weight: 600; display: block; }
                .logout-note { color: #64748b; font-size: 12px; }
                .retry-btn-small { padding: 6px 12px; background: #ef4444; color: white; border: none; border-radius: 6px; font-size: 12px; cursor: pointer; }

                /* Scrollbar */
                .ward-list-wrapper::-webkit-scrollbar { width: 6px; }
                .ward-list-wrapper::-webkit-scrollbar-track { background: transparent; }
                .ward-list-wrapper::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

                /* --- MOBILE RESPONSIVE STYLES --- */
                @media (max-width: 768px) {
                    .change-ward-container {
                        padding: 0;
                        background: #f8fafc;
                        align-items: flex-start;
                    }

                    .change-wrapper {
                        flex-direction: column;
                        height: 100vh;
                        border-radius: 0;
                        box-shadow: none;
                    }

                    .desktop-only { display: none !important; }
                    .mobile-only { display: flex !important; }

                    /* Mobile Header */
                    .info-section {
                        width: 100%;
                        padding: 16px 20px;
                        background: #111827;
                        border-bottom-left-radius: 0;
                        border-bottom-right-radius: 0;
                    }

                    .mobile-header-top {
                        align-items: center;
                        gap: 12px;
                        margin-bottom: 20px;
                        width: 100%;
                    }

                    .mobile-avatar {
                        width: 48px;
                        height: 48px;
                        border-radius: 50%;
                        background: white;
                        padding: 2px;
                    }
                    .mobile-avatar img {
                        width: 100%; height: 100%; border-radius: 50%; object-fit: cover;
                    }

                    .mobile-header-text {
                        flex: 1;
                        text-align: left;
                    }

                    .info-title-mobile {
                        font-size: 18px;
                        font-weight: 700;
                        color: white;
                        margin: 0;
                    }

                    .system-access-text-mobile {
                        font-size: 12px;
                        color: #94a3b8;
                        margin: 0;
                    }

                    /* Horizontal Dark Card */
                    .combined-dark-card {
                        flex-direction: row !important;
                        justify-content: space-between;
                        align-items: center;
                        padding: 16px;
                        margin-bottom: 0;
                    }

                    .dark-card-left {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        text-align: left;
                    }

                    .pin-icon-mobile {
                        width: 36px; height: 36px;
                        background: rgba(96, 165, 250, 0.1);
                        border-radius: 8px;
                        align-items: center;
                        justify-content: center;
                    }

                    .dark-card-text-group {
                        display: flex;
                        flex-direction: column;
                    }

                    .dark-card-right {
                        text-align: right;
                        display: flex;
                        flex-direction: column;
                    }

                    /* Body Form Area */
                    .form-section {
                        padding: 24px 20px 140px 20px;
                        background: #f8fafc;
                        overflow-y: auto;
                    }

                    .form-header {
                        margin-bottom: 16px;
                    }

                    .form-title {
                        font-size: 18px;
                    }

                    .ward-list-wrapper {
                        overflow: visible; 
                        margin-bottom: 0;
                    }

                    .version-text-mobile {
                        text-align: center;
                        font-size: 11px;
                        color: #94a3b8;
                        font-style: italic;
                        margin-top: 24px;
                    }

                    /* Sticky Bottom Action Bar */
                    .mobile-action-bar {
                        position: fixed;
                        bottom: 0;
                        left: 0;
                        width: 100%;
                        background: #f8fafc;
                        padding: 16px 20px;
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                        box-shadow: 0 -4px 12px rgba(0,0,0,0.05);
                        z-index: 50;
                    }

                    .mobile-switch-btn {
                        width: 100%;
                        padding: 14px;
                        background: #1e3a8a;
                        color: white;
                        border: none;
                        border-radius: 10px;
                        font-weight: 700;
                        font-size: 15px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .mobile-switch-btn:disabled {
                        opacity: 0.7;
                    }

                    .mobile-bottom-row {
                        display: flex;
                        gap: 12px;
                        width: 100%;
                    }

                    .mobile-secondary-btn {
                        flex: 1;
                        padding: 12px;
                        background: #f1f5f9;
                        color: #334155;
                        border: none;
                        border-radius: 10px;
                        font-weight: 600;
                        font-size: 13px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                    }
                }
            `}</style>
        </div>
    );
};

export default ChangeWard;